import React from "react";
import { getIn, type FormikProps, type FormikValues } from "formik";
import type { SxProps, Theme } from "@mui/material";
import CustomFields from "components/common/CustomFields";
import CustomFieldParentType from "enums/CustomFieldParentType";
import type {
  CustomFieldsRecord,
  FormikCustomField,
} from "hooks/customFields.types";

/** Every caller keeps its custom-field record here; none has ever moved it. */
const PATH = "customFields";

const EMPTY: CustomFieldsRecord = {};

interface FormikCustomFieldsProps<Values extends FormikValues> {
  formik: FormikProps<Values>;
  /** Pass the parent's already-fetched settings to avoid a duplicate query. */
  settings?: FormikCustomField[];
  /** Fallback for a caller whose parent does not resolve settings itself. */
  parentType?: CustomFieldParentType;
  variant?: "accordion" | "flat";
  /** Columns for the flat variant, matching the surrounding form. */
  columns?: number;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Bridges a formik form to the controlled `CustomFields` renderer. Written
 * once because eleven call sites would otherwise each re-derive the same
 * read, write and touched wiring.
 *
 * A component and not a hook: most call sites render this inside a feature
 * gate, and a hook spread into that JSX would run conditionally.
 */
function FormikCustomFields<Values extends FormikValues>({
  formik,
  settings,
  parentType,
  variant = "accordion",
  columns,
  disabled,
  sx,
}: FormikCustomFieldsProps<Values>) {
  const values =
    (getIn(formik.values, PATH) as CustomFieldsRecord | undefined) ?? EMPTY;

  return (
    <CustomFields
      fields={settings}
      parentType={settings ? undefined : parentType}
      values={values}
      onChange={(fieldId, next) =>
        formik.setFieldValue(`${PATH}.${fieldId}`, next)
      }
      // The old renderer never marked touched, so a per-field message could
      // only surface on submit.
      onBlur={(fieldId) => formik.setFieldTouched(`${PATH}.${fieldId}`, true)}
      errors={getIn(formik.errors, PATH)}
      touched={getIn(formik.touched, PATH)}
      variant={variant}
      columns={columns}
      disabled={disabled}
      sx={sx}
    />
  );
}

export default FormikCustomFields;
